import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { User } from '@shared/data-access/data-access-authentication';
import { SHOW_GLOBAL_LOADER } from '@shared/data-access/data-access-ui';
import { TSubscriptionPlanType } from '@shared/data-access/data-access-token/enum/subscription-plan-type.enum';
import { isDefined } from '@shared/utils/is-defined.utils';
import { map, Observable } from 'rxjs';
import { OrganizationAdmin, UpdateOrganizationRequest } from '../model/organization-admin.model';
import { FilterOrganizationRequest } from '../model/organization-filter-request.model';
import { FilterUserRequest } from '../model/user-filter-request.mode';
import { UserImpersonateResponse } from '../model/user-impersonate-response.model';

interface AdminUserStatistics {
  creditsUsed?: number | null;
  lastLoginDate?: string | null;
  lastSearchDate?: string | null;
  monthsTillRenewal?: number | null;
  planType?: TSubscriptionPlanType | null;
  renewalDate?: string | null;
  searchesRemaining?: number | null;
  searchesUsed?: number | null;
  subscriptionStartDate?: string | null;
  totalTopUpCreditsPurchased?: number | null;
  lastTopUpDate?: string | null;
  lastTopUpCredits?: number | null;
}

type AdminUserResponse = Omit<User, 'plan' | 'lastLoginAt' | 'lastSearchAt'> & {
  statistics?: AdminUserStatistics | null;
};

@Injectable()
export class UserService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiUrl;

  getUsers(payload: FilterUserRequest): Observable<User[] | []> {
    let params = new HttpParams();
    if (payload.search) {
      params = params.set('search', payload.search);
    }

    if (isDefined(payload.pageNumber)) {
      params = params.set('pageNumber', payload.pageNumber);
    }

    if (payload.pageSize) {
      params = params.set('pageSize', payload.pageSize);
    }

    return this.http
      .get<AdminUserResponse[]>(`${this.baseUrl}/v1/admin/users`, {
        params,
        context: new HttpContext().set(SHOW_GLOBAL_LOADER, payload.enableGlobalLoader),
      })
      .pipe(map((users) => users.map(flattenAdminUserStatistics)));
  }

  createUser(payload: Partial<User>): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/v1/admin/user`, payload);
  }

  anonymizeUser(id: string): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/v1/admin/users/${id}/anonymize`, null, {
      context: new HttpContext().set(SHOW_GLOBAL_LOADER, true),
    });
  }

  approveUser(id: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/v1/admin/users/${id}/approve`, null, {
      context: new HttpContext().set(SHOW_GLOBAL_LOADER, true),
    });
  }

  rejectUser(id: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/v1/admin/users/${id}/reject`, null, {
      context: new HttpContext().set(SHOW_GLOBAL_LOADER, true),
    });
  }

  updateUser(id: string, payload: User): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/v1/admin/users/${id}`, payload);
  }

  loginAsUser(id: string): Observable<UserImpersonateResponse> {
    return this.http.post<UserImpersonateResponse>(
      `${this.baseUrl}/v1/admin/users/${id}/impersonate`,
      {},
      { context: new HttpContext().set(SHOW_GLOBAL_LOADER, true) },
    );
  }

  updateOrganization(id: string, payload: UpdateOrganizationRequest): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/v1/organizations/${id}`, payload, {
      context: new HttpContext().set(SHOW_GLOBAL_LOADER, true),
    });
  }

  getOrganizations(payload: FilterOrganizationRequest): Observable<OrganizationAdmin[]> {
    let params = new HttpParams();

    if (payload.ordering) {
      params = params.set('ordering', payload.ordering);
    }

    if (payload.sortDirection) {
      params = params.set('sortDirection', payload.sortDirection);
    }

    if (isDefined(payload.pageNumber)) {
      params = params.set('pageNumber', payload.pageNumber);
    }

    if (payload.pageSize) {
      params = params.set('pageSize', payload.pageSize);
    }

    return this.http.get<OrganizationAdmin[]>(`${this.baseUrl}/v1/organizations`, {
      params,
      context: new HttpContext().set(SHOW_GLOBAL_LOADER, payload.enableGlobalLoader),
    });
  }
}

function flattenAdminUserStatistics({ statistics, ...user }: AdminUserResponse): User {
  return {
    ...(user as User),
    lastLoginAt: statistics?.lastLoginDate ?? undefined,
    lastSearchAt: statistics?.lastSearchDate ?? undefined,
    searchesUsed: statistics?.searchesUsed ?? undefined,
    searchesRemaining: statistics?.searchesRemaining ?? undefined,
    plan: statistics?.planType ?? undefined,
    renewalDate: statistics?.renewalDate ?? undefined,
    monthsTillRenewal: statistics?.monthsTillRenewal ?? undefined,
    subscriptionStartDate: statistics?.subscriptionStartDate ?? undefined,
    totalTopUpCreditsPurchased: statistics?.totalTopUpCreditsPurchased ?? undefined,
    lastTopUpDate: statistics?.lastTopUpDate ?? undefined,
    lastTopUpCredits: statistics?.lastTopUpCredits ?? undefined,
  };
}
